<template>
    <div :class="{ 'is-native': isMobile }">
    <div class="top-background"/>
        <main class="main-container">
        <div class="header-text-container">
            <span class="welcome-text">Hello, welcome to {{ userInfo?.member?.corporate_name || 'membership' }} 👋</span>
            <h1 class="member-name-title">{{ memberName || dashboardView.member_name }}</h1>
        </div>

        <!-- Balance Section -->
        <div class="balance-container">
            <h2 class="balance-heading">{{ isPointSystemActive ? 'MY MEMBERSHIP BALANCE' : 'MY MEMBERSHIP TIER' }}</h2>
            <!-- When Point System is NONACTIVE - Show Tier Progress -->
            <div v-if="!isPointSystemActive" class="balance-card-container">
                <div class="tier-progress-wrapper">
                    <member-tier-progress
                        v-if="currentTierData"
                        :state="currentTierData.state"
                        :progress-percentage="currentTierData.progressPercentage"
                        :current-amount="currentTierData.currentAmount"
                        :current-tier="currentTierData.currentTierData"
                        :next-tier="currentTierData.nextTierData"
                        :is-selected="true"
                    />
                    <div v-else class="tier-loading">Loading tier progress...</div>
                </div>
            </div>
            <!-- When Point System is ACTIVE - Show Tier, Points, and Vouchers -->
            <div v-else class="balance-card-container">
                <div class="balance-card">
                    <img 
                        v-if="dashboardView.tier && dashboardView.tier.icon && dashboardView.tier.icon.image_url && !dashboardView.tier.icon.image_url.includes('fas ')"
                        :src="dashboardView.tier.icon.image_url" 
                        alt="Tier Icon"
                        width="48"
                        style="object-fit: contain;">
                    <i 
                        v-else-if="dashboardView?.tier?.icon?.image_url?.includes('fas ') || dashboardView?.tier_name === 'Member' || !dashboardView?.tier_name || dashboardView?.tier_name === '-'"
                        class="fas fa-medal fa-3x medal-icon">
                    </i>
                    <img 
                        v-else
                        src="/img/member-icon/tier-logo.svg" 
                        alt="Tier Icon"
                        width="48"
                        style="object-fit: contain;">
                    <div class="balance-card-title">
                        <span class="balance-title">TIER</span>
                        <span class="balance-value">{{ dashboardView.tier_name && dashboardView.tier_name !== '-' ? (dashboardView.tier_name.length > 25 ? dashboardView.tier_name.substring(0, 25) : dashboardView.tier_name) : 'Member' }}</span>
                    </div>
                </div>
                <div class="balance-card">
                    <img src="/img/member-icon/points-logo.svg" alt="Points" width="48">
                    <div class="balance-card-title">
                        <span class="balance-title">POINTS</span>
                        <span class="balance-value">{{ dashboardView.points }}</span>
                        <div v-if="isPointValueVisibleToMember" class="balance-estimated">
                            <span class="estimated-value">IDR {{ formatCurrency(dashboardView.point_balance || 0) }}</span>
                            <span class="estimated-label">Total Estimated Value</span>
                        </div>
                    </div>
                </div>
                <div class="balance-card">
                    <img src="/img/member-icon/voucher-logo.svg" alt="Vouchers" width="48">
                    <div class="balance-card-title">
                        <span class="balance-title">VOUCHERS</span>
                        <span class="balance-value">{{ dashboardView.owned_vouchers }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Booking Engine Connected Merchants Section -->
        <member-booking-merchant-card
            v-if="isBookingModuleEnabled"
            :merchants="bookingMerchants"
            :auth-context="bookingAuthContext"
            :loading="isBookingLoading"
        />

        <!-- Promotions Section -->
        <template v-if="isPromotionsModuleEnabled">
        <section class="section-container" v-if="promotionItems && promotionItems.length > 0">
            <div class="section-heading">
                <h2 class="section-title">{{ promotionTitle }}</h2>
                <span class="section-view-all" @click="handleViewAllPromotions">View all</span>
            </div>

            <div v-if="redeemStatus.message" :class="['redeem-feedback', redeemStatus.type === 'error' ? 'redeem-feedback--error' : 'redeem-feedback--success']">
                {{ redeemStatus.message }}
            </div>

            <div class="voucher-list">
                <template v-for="(item, index) in promotionItems" :key="item.id || index">
                    <member-voucher-content
                        v-if="item.item_type === 'voucher' && isPointSystemActive"
                        :voucher="item"
                        :disabled="!isPointSystemActive"
                        :is-locked="item.isLocked"
                        :required-tier-name="item.requiredTierName"
                        @click="() => handlePromotionItemClick(item)"
                    />
                    <member-benefit-content
                        v-else-if="item.item_type === 'benefit'"
                        :benefit="item"
                        :is-locked="item.isLocked"
                        :required-tier-name="item.requiredTierName"
                        @click="() => handlePromotionItemClick(item)"
                    />
                </template>
            </div>
        </section>
         
        <section class="section-container" v-else-if="dashboardView && (!promotionItems || promotionItems.length === 0)">
            <div class="section-heading">
                <h2 class="section-title">{{ promotionTitle }}</h2>
            </div>
            <p style="color: #999; text-align: center; padding: 20px;">No promotions available at the moment</p>
        </section>
        </template>

        <!-- Newsletter Section -->
        <section v-if="isNewsletterModuleEnabled" class="section-container">
            <member-newsletter-widget
                :newsletters="newsletters"
                :loading="loadingNewsletters"
            />
        </section>

        <!-- My Recent Transaction Section -->
        <section class="section-container">
            <div class="section-heading">
                <h2 class="section-title">My Recent Transactions</h2>
                <span class="section-view-all" @click="router.push('/client/transaction-history')">View all</span>
            </div>

            <div class="transaction-list">
                <my-transaction 
                    v-for="(transaction, index) in getRecentTransactions()" 
                    :key="transaction.id || index"
                    :transaction="transaction"
                    :vouchers="dashboardView?.vouchers || []"
                    @open-receipt="showModal(transaction, dashboardView)"
                />
                <div v-if="!getRecentTransactions().length" style="color: #999; text-align: center; padding: 20px;">
                    No recent transactions
                </div>
            </div>
        </section>

        <!-- My Benefits Sections -->
        <template v-if="isBenefitModuleEnabled">
            <section class="section-container" v-if="benefitsToShow.length">
                <div class="section-heading">
                    <h2 class="section-title">My Benefits</h2>
                    <span class="section-view-all" @click="redirectToBenefits">View all</span>
                </div>

                <div class="benefits-list">
                    <my-benefit 
                        v-for="(benefit, index) in benefitsToShow"
                        :key="benefit.id || index"
                        :benefit="benefit"
                        @select="showBenefitDetail"
                    />
                    <button 
                        v-if="remainingBenefitsCount > 0"
                        class="benefit-more"
                        @click="redirectToBenefits"
                    >
                        +{{ remainingBenefitsCount }} more
                    </button>
                </div>
            </section>
            <section class="section-container" v-else>
                <div class="section-heading">
                    <h2 class="section-title">My benefits</h2>
                </div>
                <div style="color: #999; text-align: center; padding: 20px;">
                    No benefits available at the moment
                </div>
            </section>
        </template>
          
        <benefit-detail-modal
            v-if="benefitModalVisible && selectedBenefit"
            :benefit="selectedBenefit"
            @close="closeBenefitModal"
        />

        <voucher-detail-modal
            v-if="voucherModalVisible && selectedVoucher"
            :voucher="selectedVoucher"
            :disabled="!isPointSystemActive"
            @close="closeVoucherModal"
            @redeem="redeemVoucher"
        />

        <voucher-redeem-status-modal
            v-if="redeemStatusModal.visible"
            :status="redeemStatusModal.status"
            @close="handleRedeemStatusClose"
        />

        <tier-upgrade-modal
            v-if="tierUpgradeModalVisible && selectedLockedItem"
            :item-name="selectedLockedItem.name"
            :item-type="selectedLockedItem.item_type || 'voucher'"
            :required-tier-name="selectedLockedItem.requiredTierName"
            :required-tier-icon="selectedLockedItem.requiredTier?.icon?.image_url"
            :current-tier-name="dashboardView?.tier_name || 'Bronze'"
            :current-tier-icon="dashboardView?.tier?.icon?.image_url"
            @close="closeTierUpgradeModal"
            @view-tier-benefits="handleViewTierBenefits"
        />

        <all-promotions-modal
            v-if="allPromotionsModalVisible"
            :items="allPromotionItems"
            :title="promotionTitle"
            @close="closeAllPromotionsModal"
            @click-item="handlePromotionItemClick"
        />

    </main>

    <transaction-client
        v-if="isModalVisible"
        @parentCloseModal="closeModal"
        :editedItem="editedItem"
    />

    <!-- Gift Info Modal -->
    <div v-if="isGiftModalOpen" class="gift-modal-overlay" @click="closeModal">
        <div class="gift-modal-content" @click.stop>
            <div class="gift-modal-header">
                <div class="gift-modal-icon">🎁</div>
                <button @click="closeModal" class="gift-modal-close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>
            <div class="gift-modal-body">
                <h2 class="gift-modal-title">{{ giftItem.detail_name }}</h2>
                <p class="gift-modal-subtitle">
                    <span v-if="giftItem.branch_name || giftItem.merchant_name">
                        {{ giftItem.branch_name }} <span v-if="giftItem.branch_name && giftItem.merchant_name"> • </span> {{ giftItem.merchant_name }}
                    </span>
                    <span v-else>{{ giftItem.corporate_name }}</span>
                </p>
                <p class="gift-modal-date">Diterima: {{ formatGiftDate(giftItem.created_at) }}</p>

                <div v-if="giftItem.gift_note" class="gift-modal-note">
                    <p class="gift-note-label">Pesan dari merchant:</p>
                    <p class="gift-note-text">{{ giftItem.gift_note }}</p>
                </div>
                <div v-else class="gift-modal-note gift-note-empty">
                    <p class="gift-note-text">Tidak ada pesan dari merchant.</p>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<style scoped>
.benefits-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.benefit-more {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
    padding: 12px 16px;
    background: white;
    border-radius: 12px;
    border: 1px dashed #E1E4EA;
    color: var(--primary-color, #F93E3E);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    width: 100%;
}

.benefit-more:hover {
    background: var(--secondary-color, #FFF5F5);
}

.transaction-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.voucher-list {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding-bottom: 10px;
    scrollbar-width: thin;
    scrollbar-color: rgba(209, 213, 219, 0.8) transparent;
}

.redeem-feedback {
    margin-bottom: 12px;
    padding: 12px 16px;
    border-radius: 10px;
    font-size: 12px;
    font-weight: 600;
}

.redeem-feedback--success {
    background: rgba(74, 222, 128, 0.16);
    color: #15803D;
}

.redeem-feedback--error {
    background: rgba(248, 113, 113, 0.16);
    color: #B91C1C;
}

.voucher-list::-webkit-scrollbar {
    height: 3px;
}

.voucher-list::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 99px;
}

.voucher-list::-webkit-scrollbar-thumb {
    background: rgba(209, 213, 219, 0.8);
    border-radius: 99px;
    transition: background 0.2s ease;
}

.voucher-list::-webkit-scrollbar-thumb:hover {
    background: rgba(156, 163, 175, 0.9);
}

.main-container {
    height: fit-content;
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 24px;
    box-sizing: border-box;
}
.top-background {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -50;
    background-color: var(--primary-bg, #F93E3E);
    height: 194px;
    width: 100%;
}

.balance-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.balance-heading {
    color: #717784;
    font-size: 12px;
    font-weight: normal;
}

.header-text-container {
    display: flex;
    flex-direction: column;
    gap: 8px;
    color: white;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
}

.welcome-text {
    font-size: 10px;
    font-weight: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
}

.member-name-title {
    font-size: 1.2rem;
    font-weight: 520;
    letter-spacing: -2%;
    word-break: break-word;
    overflow-wrap: anywhere;
    line-height: 1.3;
}

.balance-card-container {
    display: flex;
    justify-content: space-between;
}

.tier-progress-wrapper {
    width: 100%;
    padding: 16px;
    background: transparent;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    justify-content: center;
    align-items: center;
}

.tier-progress-title {
    font-size: 14px;
    font-weight: 600;
    color: #1B1E28;
    margin-bottom: 16px;
    text-align: center;
}

.tier-loading {
    text-align: center;
    color: #717784;
    padding: 24px;
    font-size: 14px;
}

.balance-value--large {
    font-size: 1.25rem !important;
}

.balance-card {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.balance-card-title {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    width: auto;
    min-width: 78px;
    gap: 4px;
}

.balance-title {
    font-size: 0.5rem;
    font-weight: normal;
    min-height: 12px;
}

.balance-value {
    font-size: 1rem;
    font-weight: bold;
    min-height: 24px;
    display: flex;
    align-items: center;
}

.balance-estimated {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0px;
    margin-top: 4px;
    padding: 4px 6px;
    background: var(--secondary-color, rgba(249, 62, 62, 0.06));
    border-radius: 4px;
}

.balance-estimated .estimated-value {
    font-size: 0.6rem;
    font-weight: 600;
    color: var(--primary-color, #F93E3E);
    white-space: nowrap;
    letter-spacing: -0.01em;
    line-height: 1.5;
}

.balance-estimated .estimated-label {
    font-size: 0.4rem;
    font-weight: 500;
    color: #717784;
    white-space: nowrap;
    letter-spacing: 0.02em;
    line-height: 1.5;
}

.section-container {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

/* Section Headings */
.section-heading {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 70%;
}

.section-view-all {
    font-size: 14px;
    color: #6B7280;
    cursor: pointer;
    white-space: nowrap;
    flex-shrink: 0;
}

.section-view-all:hover {
    color: var(--primary-color, #F93E3E);
}

.medal-icon {
    color: var(--primary-icon, #f93e3e);
    margin-bottom: 5px;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes slideUp {
    from {
        transform: translateY(20px);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

.gift-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    backdrop-filter: blur(2px);
    animation: fadeIn 0.2s ease;
}

.gift-modal-content {
    background: #fff;
    border-radius: 20px;
    width: 100%;
    max-width: 420px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
    overflow: hidden;
    animation: slideUp 0.25s ease;
}

.gift-modal-header {
    background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 20px 16px;
}

.gift-modal-icon {
    font-size: 40px;
    line-height: 1;
}

.gift-modal-close {
    background: rgba(255, 255, 255, 0.7);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #065F46;
    transition: background 0.2s;
}

.gift-modal-close:hover {
    background: rgba(255, 255, 255, 0.95);
}

.gift-modal-body {
    padding: 20px 24px 28px;
}

.gift-modal-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 6px;
    line-height: 1.3;
}

.gift-modal-subtitle {
    font-size: 13px;
    color: #6B7280;
    margin: 0 0 4px;
}

.gift-modal-date {
    font-size: 12px;
    color: #9CA3AF;
    margin: 0 0 20px;
}

.gift-modal-note {
    background: #F0FDF4;
    border: 1px solid #BBF7D0;
    border-radius: 12px;
    padding: 14px 16px;
}

.gift-modal-note.gift-note-empty {
    background: #F9FAFB;
    border-color: #E5E7EB;
}

.gift-note-label {
    font-size: 11px;
    font-weight: 600;
    color: #065F46;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin: 0 0 6px;
}

.gift-note-text {
    font-size: 14px;
    color: #374151;
    line-height: 1.6;
    margin: 0;
    white-space: pre-wrap;
}

.gift-note-empty .gift-note-text {
    color: #9CA3AF;
    font-style: italic;
}

/* Newsletter Empty State (when feature flag is NONACTIVE) */
.newsletter-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    background: #f9fafb;
    border-radius: 12px;
    border: 1px dashed #d1d5db;
    text-align: center;
}

.newsletter-empty-state i {
    font-size: 28px;
    color: #d1d5db;
    margin-bottom: 10px;
}

.newsletter-empty-state p {
    font-size: 13px;
    color: #9ca3af;
    margin: 0;
}

.empty-feature-state {
    opacity: 0.7;
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
}

.empty-feature-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 0;
}

.empty-feature-content i {
    font-size: 24px;
    opacity: 0.5;
}
</style>

<script>
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { onMounted, ref, computed, watch, onBeforeUnmount } from '@vue/runtime-core'
import { Capacitor } from '@capacitor/core'
import TransactionClient from './MemberTransaction.vue'
import MemberVoucherContent from '@/components/member/MemberVoucherContent.vue'
import MemberBenefitContent from '@/components/member/MemberBenefitContent.vue'
import MyTransaction from '../../components/member/MyTransaction.vue'
import MyBenefit from '../../components/member/MyBenefit.vue'
import BenefitDetailModal from '@/components/member/BenefitDetailModal.vue'
import VoucherDetailModal from '@/components/member/VoucherDetailModal.vue'
import VoucherRedeemStatusModal from '@/components/member/VoucherRedeemStatusModal.vue'
import TierUpgradeModal from '@/components/member/TierUpgradeModal.vue'
import AllPromotionsModal from '@/components/member/AllPromotionsModal.vue'
import { usePointSystem } from '@/composables/pointsystem'
import MemberTierProgress from '@/components/member/MemberTierProgress.vue'
import MemberNewsletterWidget from '@/components/member/MemberNewsletterWidget.vue'
import MemberBookingMerchantCard from '@/components/member/MemberBookingMerchantCard.vue'
import { useModalHistory } from '@/composables/useModalHistory'

export default {
    name: 'MemberDashboardMobile',
    props: ['dashboard'],
    components: {
        TransactionClient,
        MemberVoucherContent,
        MemberBenefitContent,
        MyTransaction,
        MyBenefit,
        BenefitDetailModal,
        VoucherDetailModal,
        VoucherRedeemStatusModal,
        TierUpgradeModal,
        AllPromotionsModal,
        MemberTierProgress,
        MemberNewsletterWidget,
        MemberBookingMerchantCard
    },
    setup(props) {
        const { isPointSystemActive, isPointValueEnabled, isPointValueVisibleToMember } = usePointSystem()
        const store = useStore();
        const router = useRouter();
        const isMobile = Capacitor.isNativePlatform();

        // State untuk transaction modal
        const isModalVisible = ref(false);
        const isGiftModalOpen = ref(false);
        const giftItem = ref({});
        const editedItem = ref({
            id: '',
            name: '',
            transaction_value: '',
            title: ''
        });

        // State untuk dashboard data
        const dashboardData = ref(props.dashboard ? { ...props.dashboard } : {});

        // State untuk benefits
        const benefits = ref([]);
        const displayedBenefitsLimit = 5;
        const selectedBenefit = ref(null);
        const benefitModalVisible = ref(false);

        // State untuk vouchers
        const selectedVoucher = ref(null);
        const voucherModalVisible = ref(false);
        const redeemStatus = ref({ type: '', message: '' });
        const redeemStatusModal = ref({ visible: false, status: 'loading' });

        // State untuk tier upgrade modal
        const tierUpgradeModalVisible = ref(false);
        const selectedLockedItem = ref(null);
        
        // State for All Promotions Modal
        const allPromotionsModalVisible = ref(false);

        // Tier progress data
        const allTiers = ref([]);
        const totalTransaction = ref(0);
        const currentTierName = ref('');
        const currentTierData = ref(null);

        const newsletters = ref([]);
        const loadingNewsletters = ref(false);

        // Booking Engine Connected Merchants state
        const isBookingModuleEnabled = computed(() => {
            const flagObj = store.state.FeatureFlag?.bookingModule;
            if (flagObj) {
                return flagObj.flag === 'ACTIVE';
            }
            if (store.state.FeatureFlag?.loading) {
                return false;
            }
            return true;
        });
        const bookingMerchants = computed(() => store.getters['ClientBooking/connectedMerchants'] || []);
        const bookingAuthContext = computed(() => store.getters['ClientBooking/authContext'] || null);
        const isBookingLoading = computed(() => {
            return isBookingModuleEnabled.value && Boolean(store.getters['ClientBooking/isLoading']);
        });

        const fetchBookingMerchants = async () => {
            if (!isBookingModuleEnabled.value) return;
            try {
                await store.dispatch('ClientBooking/fetchConnectedMerchants');
            } catch (error) {
                console.error('Error fetching connected booking merchants:', error);
            }
        };

        // Check if newsletter module is enabled via feature flag
        const isNewsletterModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.newsletterModule?.flag || 'ACTIVE') === 'ACTIVE';
        });

        // Modal History (Back Button Handler)
        const { pushModalHistory: pushTransactionModal, popModalHistory: popTransactionModal } = useModalHistory({
            isVisible: isModalVisible,
            onClose: () => { isModalVisible.value = false },
            stateKey: 'dashboard-transaction-modal'
        })

        const { pushModalHistory: pushBenefitModal, popModalHistory: popBenefitModal } = useModalHistory({
            isVisible: benefitModalVisible,
            onClose: () => {
                benefitModalVisible.value = false
                selectedBenefit.value = null
            },
            stateKey: 'dashboard-benefit-modal'
        })

        const { pushModalHistory: pushVoucherModal, popModalHistory: popVoucherModal } = useModalHistory({
            isVisible: voucherModalVisible,
            onClose: () => {
                voucherModalVisible.value = false
                selectedVoucher.value = null
            },
            stateKey: 'dashboard-voucher-modal'
        })

        const { pushModalHistory: pushTierUpgradeModal, popModalHistory: popTierUpgradeModal } = useModalHistory({
            isVisible: tierUpgradeModalVisible,
            onClose: () => {
                tierUpgradeModalVisible.value = false
                selectedLockedItem.value = null
            },
            stateKey: 'dashboard-tier-upgrade-modal'
        })

        const { pushModalHistory: pushAllPromotionsModal, popModalHistory: popAllPromotionsModal } = useModalHistory({
            isVisible: allPromotionsModalVisible,
            onClose: () => { allPromotionsModalVisible.value = false },
            stateKey: 'dashboard-all-promotions-modal'
        })

        const { pushModalHistory: pushGiftModal, popModalHistory: popGiftModal } = useModalHistory({
            isVisible: isGiftModalOpen,
            onClose: () => { isGiftModalOpen.value = false },
            stateKey: 'dashboard-gift-modal'
        })

        onBeforeUnmount(() => {
            document.body.style.overflow = ''
        })

        watch([isModalVisible, isGiftModalOpen, benefitModalVisible, voucherModalVisible, tierUpgradeModalVisible, allPromotionsModalVisible], (vals) => {
            const anyOpen = vals.some(Boolean)
            document.body.style.overflow = anyOpen ? 'hidden' : ''
        })

        /**
         * Computed property untuk memeriksa apakah dashboard memiliki data
         */
        const hasDashboardData = computed(() => Object.keys(dashboardData.value || {}).length > 0);

        /**
         * Computed property untuk mengambil userInfo dari Auth store
         */
        const userInfo = computed(() => store.state.Auth.userInfo);

        /**
         * Computed property untuk menampilkan dashboard view
         * Prioritas: dashboardData internal > props.dashboard
         */
        const dashboardView = computed(() => hasDashboardData.value ? dashboardData.value : (props.dashboard || {}));

        /**
         * Computed property untuk menampilkan benefits dengan limit
         */
        const benefitsToShow = computed(() => {
            if (!Array.isArray(benefits.value)) return [];
            return benefits.value.slice(0, displayedBenefitsLimit);
        });

        /**
         * Computed property untuk memeriksa apakah benefit module enabled
         */
        const isBenefitModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.benefitModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        const isPointControlEnabled = computed(() => {
            const flag = store.state.FeatureFlag?.pointControl?.flag;
            return flag === 'CENTRALIZE' || flag === 'DECENTRALIZE';
        })

        const isPromotionsModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.promotionsModule?.flag || 'ACTIVE') === 'ACTIVE'
        })

        /**
         * Computed property untuk menghitung sisa benefits yang tidak ditampilkan
         */
        const remainingBenefitsCount = computed(() => {
            if (!Array.isArray(benefits.value)) return 0;
            const remaining = benefits.value.length - displayedBenefitsLimit;
            return remaining > 0 ? remaining : 0;
        });

        /**
         * Computed property untuk title promotion section
         * Menggunakan title dari promotion_section API atau default
         */
        const promotionTitle = computed(() => {
            return dashboardView.value?.promotion_section?.title || 'Limited offers';
        });

        /**
         * Computed property untuk items promotion section
         * Termasuk locked items dengan tier comparison logic
         */
        const allPromotionItems = computed(() => {
            const promotionSection = dashboardView.value?.promotion_section;
            if (!promotionSection?.items || !Array.isArray(promotionSection.items)) return [];
            
            const now = new Date();
             // displayLimit logic moved to promotionItems
            const currentTier = dashboardView.value?.tier;
            const currentTierMinTransaction = parseFloat(currentTier?.transaction_value_min || 0);
            
            // Filter items based on expiry and tier visibility (higher tiers don't see lower tier vouchers)
            const filteredItems = promotionSection.items
                .filter(item => {
                    // Check expiry
                    if (item.expiration) {
                        const expirationDate = new Date(item.expiration);
                        if (expirationDate <= now) return false;
                    }

                    // Check tier visibility (Deals match: high tier hides low tier vouchers)
                    if (item.member_tier_id || item.tier_id) {
                        const itemTier = item.tier;
                        if (itemTier) {
                            const itemTierMinTransaction = parseFloat(itemTier.transaction_value_min || 0);
                            // If member tier is higher than voucher tier, hide it
                            if (currentTierMinTransaction > itemTierMinTransaction) return false;
                        }
                    }
                    
                    return true;
                })
                .map(item => {
                    // Determine if item is locked based on tier requirement
                    // Cari tier data dari item atau fallback ke member_tier_id
                    let requiredTier = null;
                    let isLocked = false;
                    
                    if (item.member_tier_id || item.tier_id) {
                        // Jika ada member_tier_id atau tier_id, cari tier data dari item
                        requiredTier = item.tier || { 
                            name: 'Higher Tier', 
                            transaction_value_min: '999999999' 
                        };
                        
                        const itemTierMinTransaction = parseFloat(requiredTier.transaction_value_min || '999999999');
                        isLocked = itemTierMinTransaction > currentTierMinTransaction;
                    }
                    
                    return {
                        ...item,
                        isLocked,
                        requiredTierName: isLocked ? requiredTier?.name || 'Higher Tier' : null,
                        requiredTier
                    };
                })
                .sort((a, b) => {
                    const sortOrderA = parseInt(a.sort_order) || 999;
                    const sortOrderB = parseInt(b.sort_order) || 999;
                    return sortOrderA - sortOrderB;
                });
            let finalItems = filteredItems;
            
            if (!isBenefitModuleEnabled.value) {
                finalItems = finalItems.filter(item => item.item_type !== 'benefit');
            }
            if (!isPointControlEnabled.value) {
                finalItems = finalItems.filter(item => item.item_type !== 'voucher');
            }
            
            return finalItems;
        });

        const promotionItems = computed(() => {
             const displayLimit = dashboardView.value?.promotion_section?.display_limit || 5;
             return allPromotionItems.value.slice(0, displayLimit);
        });

        /**
         * Watcher untuk memantau perubahan props dashboard
         * Update dashboardData jika props berubah dan data internal belum ada
         */
        watch(
            () => props.dashboard,
            (newVal) => {
                if (newVal && !hasDashboardData.value) {
                    dashboardData.value = { ...newVal };
                }
            },
            { immediate: false }
        );

        watch(isBookingModuleEnabled, (enabled) => {
            if (enabled && bookingMerchants.value.length === 0) {
                fetchBookingMerchants();
            }
        });

        /**
         * Lifecycle hook yang dijalankan saat component mounted
         * Memuat semua data yang diperlukan
         */
        onMounted(() => {
            loadAllData();
        });

        /**
         * Memuat semua data dashboard, benefits, dan vouchers secara bersamaan
         * @async
         */
        const loadAllData = async () => {
            try {
                await Promise.all([
                    fetchDashboard(),
                    loadBenefitsData(),
                    fetchNewsletters(),
                    fetchBookingMerchants()
                ]);
                
                if (!isPointSystemActive.value) {
                    await loadTierData();
                }
            } catch (error) {
                console.error('Error loading all data:', error);
            }
        };

        /**
         * Mengambil data dashboard dari API dan update state
         * @async
         */
        async function fetchDashboard() {
            try {
                const response = await store.dispatch('ClientDashboard/read');
                const latestDashboard = response?.data?.data;
                if (latestDashboard) {
                    dashboardData.value = { ...latestDashboard };
                }
            } catch (error) {
                console.error('Error loading dashboard data:', error);
            }
        }

        /**
         * Mengambil data benefits dari API
         * @async
         */
        async function loadBenefitsData() {
            try {
                const dashboardData = await store.dispatch('ClientBenefit/read');
                const data = dashboardData.data.data;

                if (data.benefit && typeof data.benefit === 'object') {
                    benefits.value = Object.values(data.benefit);
                }
            } catch (error) {
                console.error('Error load benefits data:', error);
            }
        }

        const fetchNewsletters = async () => {
            loadingNewsletters.value = true;
            try {
                const response = await store.dispatch('ClientNewsletter/read', { limit: 5 });
                const data = response?.data?.data || response?.data;
                
                if (data?.data && Array.isArray(data.data)) {
                    newsletters.value = data.data;
                } else if (Array.isArray(data)) {
                    newsletters.value = data;
                }
            } catch (error) {
                console.error('Error fetching newsletters:', error);
            } finally {
                loadingNewsletters.value = false;
            }
        };

        /**
         * Format number to IDR format
         */
        function formatCurrency(value) {
            if (!value && value !== 0) return '0';
            return new Intl.NumberFormat('id-ID').format(value);
        }

        /**
         * Determine tier state based on member's current tier
         */
        function getTierState(tierIndex, currentIndex, isLastTier) {
            if (tierIndex < currentIndex) return 'passed';
            if (tierIndex === currentIndex) {
                if (isLastTier && tierIndex === allTiers.value.length - 1) return 'max';
                return 'active';
            }
            return 'locked';
        }

        /**
         * Calculate progress percentage for a specific tier
         */
        function calculateTierProgress(tierIndex, currentIndex, tierData, nextTierData) {
            if (tierIndex < currentIndex) return 100;
            if (tierIndex > currentIndex) return 0;
            
            const tierMin = parseFloat(tierData?.transaction_value_min) || 0;
            const nextMin = parseFloat(nextTierData?.transaction_value_min) || tierMin;
            
            if (!nextTierData || nextMin <= tierMin) return 100;
            
            const range = nextMin - tierMin;
            const currentProgress = totalTransaction.value - tierMin;
            
            if (currentProgress <= 0) return 0;
            if (currentProgress >= range) return 100;
            
            return (currentProgress / range) * 100;
        }

        /**
         * Load tier data from ClientBenefit API
         */
        async function loadTierData() {
            try {
                const response = await store.dispatch('ClientBenefit/read');
                const data = response.data.data;
                
                allTiers.value = data.tier || [];
                totalTransaction.value = data.total_transaction || 0;
                currentTierName.value = data.tier_name || '';
                
                if (allTiers.value.length > 0) {
                    const sortedTiers = [...allTiers.value].sort((a, b) => parseFloat(a.transaction_value_min) - parseFloat(b.transaction_value_min));
                    const currentIndex = sortedTiers.findIndex(t => t.name === currentTierName.value);
                    
                    let currentTier, nextTier, state, progressPercentage;

                    if (currentIndex === -1) {
                        currentTier = {
                            name: currentTierName.value || 'Member',
                            transaction_value_min: 0,
                            icon: { image_url: 'fas fa-medal' }
                        };
                        nextTier = sortedTiers[0] || null;
                        state = 'active';

                        const nextMin = parseFloat(nextTier?.transaction_value_min) || 0;
                        if (nextMin > 0 && totalTransaction.value > 0) {
                            progressPercentage = (totalTransaction.value / nextMin) * 100;
                            if (progressPercentage > 100) progressPercentage = 100;
                        } else {
                            progressPercentage = 0;
                        }
                    } else {
                        const isAtMaxTier = currentIndex === sortedTiers.length - 1;
                        currentTier = sortedTiers[currentIndex];
                        nextTier = sortedTiers[currentIndex + 1] || null;
                        state = getTierState(currentIndex, currentIndex, isAtMaxTier);
                        progressPercentage = calculateTierProgress(currentIndex, currentIndex, currentTier, nextTier);
                    }
                    
                    currentTierData.value = {
                        state,
                        progressPercentage,
                        currentAmount: formatCurrency(totalTransaction.value),
                        currentTierData: {
                            name: currentTier.name,
                            amount: currentIndex === -1 ? `IDR. 0` : `IDR. ${formatCurrency(currentTier.transaction_value_min)}`,
                            icon: currentTier.icon?.image_url || (currentIndex === -1 ? 'fas fa-medal' : '/img/member-img/tier-gold-example.svg')
                        },
                        nextTierData: nextTier ? {
                            name: nextTier.name,
                            amount: `IDR. ${formatCurrency(nextTier.transaction_value_min)}`,
                            icon: nextTier.icon?.image_url || '/img/member-img/tier-platinum-example.svg'
                        } : {
                            name: 'MAX',
                            amount: 'Maximum Tier',
                            icon: currentTier.icon?.image_url || '/img/member-img/tier-platinum-example.svg'
                        }
                    };
                }
            } catch (error) {
                console.error('Error loading tier data:', error);
            }
        }

        /**
         * Menampilkan modal detail benefit
         * @param {Object} benefit - Data benefit yang akan ditampilkan
         */
        function showBenefitDetail(benefit) {
            selectedBenefit.value = benefit;
            benefitModalVisible.value = true;
            pushBenefitModal();
        }

        /**
         * Menutup modal detail benefit
         */
        function closeBenefitModal() {
            benefitModalVisible.value = false;
            selectedBenefit.value = null;
            popBenefitModal();
        }

        /**
         * Redirect ke halaman benefits
         */
        function redirectToBenefits() {
            router.push('/client/benefit');
        }

        /**
         * Menampilkan modal detail voucher
         * @param {Object} voucher - Data voucher yang akan ditampilkan
         */
        function showVoucherDetail(voucher) {
            selectedVoucher.value = {
                ...voucher,
                id: voucher?.id ?? voucher?.voucher_id ?? voucher?.voucherId
            };
            voucherModalVisible.value = true;
            pushVoucherModal();
        }

        /**
         * Menutup modal detail voucher
         */
        function closeVoucherModal() {
            voucherModalVisible.value = false;
            selectedVoucher.value = null;
            popVoucherModal();
        }

        /**
         * Handler untuk click pada promotion item
         * Menentukan behavior berdasarkan item_type dan locked status
         * @param {Object} item - Data promotion item
         */
        function handlePromotionItemClick(item) {
            // If item is locked, show tier upgrade modal
            if (item.isLocked) {
                selectedLockedItem.value = item;
                tierUpgradeModalVisible.value = true;
                pushTierUpgradeModal();
                return;
            }
            
            const itemType = item?.item_type || 'voucher';
            
            if (itemType === 'benefit') {
                showBenefitDetail(item);
            } else {
                showVoucherDetail(item);
            }
        }

        /**
         * Menutup tier upgrade modal
         */
        function closeTierUpgradeModal() {
            tierUpgradeModalVisible.value = false;
            selectedLockedItem.value = null;
            popTierUpgradeModal();
        }

        /**
         * Handler untuk view tier benefits dari modal
         */
        function handleViewTierBenefits() {
            closeTierUpgradeModal();
            
            setTimeout(() => {
                router.push('/client/benefit');             }, 300);
        }

        /**
         * Handler untuk redeem voucher dari modal
         * @param {Object} voucher - Data voucher yang akan di-redeem
         */
        function redeemVoucher(voucher) {
            closeVoucherModal();
            redeemDeal(voucher);
        }

        /**
         * Menampilkan modal receipt transaksi
         * @param {Object} data - Data transaksi
         * @param {Object} member - Data member
         */
        function showModal(data, member) {
            // Gift vouchers open the gift info modal instead
            if (data.type === 'gift') {
                giftItem.value = data;
                isGiftModalOpen.value = true;
                pushGiftModal();
                return;
            }

            editedItem.value = {
                id: data.id,
                member_name: member.member_name,
                member_email: member.member_email,
                phone: member.member_profile.phone,
                state: member.member_profile.state,
                address: member.member_profile.address,
                country: member.member_profile.country,
                merchant_id: data.merchant_id,
                corporate_name: data.corporate_name,
                branch_name: data.branch_name,
                merchant_name: data.merchant_name,
                type: data.type,
                created_at: data.created_at,
                qty: data.qty,
                detail_name: data.detail_name || 'Deduction Point',
                detail_code: data.detail_code,
                transaction_code: data.transaction_code,
                transaction_value: data.subtotal,
                voucher_discount: data.voucher_discount,
                amount_after_voucher: data.amount_after_voucher,
                voucher_detail: data.voucher_detail,
                point: (data.point !== undefined && data.point !== null)
                    ? data.point
                    : (data.point_deducted !== undefined && data.point_deducted !== null)
                        ? `-${Math.abs(data.point_deducted)}`
                        : null,
                business_logo: data.merchant?.business_profile?.logo?.image_url ||
                    data.merchant?.businessProfile?.logo?.image_url ||
                    data.branch?.business_profile?.logo?.image_url ||
                    data.branch?.businessProfile?.logo?.image_url ||
                    member.branch_images?.[0]?.business_profile?.logo?.image_url ||
                    member.branch_images?.[0]?.businessProfile?.logo?.image_url,
                fallback_logo: data.branch?.business_profile?.logo?.image_url ||
                    data.branch?.businessProfile?.logo?.image_url ||
                    member.branch_images?.[0]?.business_profile?.logo?.image_url ||
                    member.branch_images?.[0]?.businessProfile?.logo?.image_url,
                reference_code: data.refference_code,
                is_pending: data.is_pending,
                tax_charge: data.tax_charge,
                service_charge: data.service_charge,
                tax_value: data.tax_value,
                service_value: data.service_value,
                before_tax_service: data.before_tax_service,
                after_tax_service: data.after_tax_service,
                is_nett_price: data.is_nett_price,
                title: 'My Transaction Receipt'
            };
            isModalVisible.value = true;
            pushTransactionModal();
        }

        /**
         * Menutup modal receipt transaksi
         */
        function closeModal() {
            if (isModalVisible.value) {
                isModalVisible.value = false;
                popTransactionModal();
            }
            if (isGiftModalOpen.value) {
                isGiftModalOpen.value = false;
                popGiftModal();
            }
        }

        function formatGiftDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const day = String(date.getDate()).padStart(2, '0');
            const month = months[date.getMonth()];
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const period = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            return `${day} ${month} ${year} • ${String(hours).padStart(2, '0')}.${minutes} ${period}`;
        }

        /**
         * Proses redeem voucher ke API
         * @async
         * @param {Object} voucher - Data voucher yang akan di-redeem
         */
        function redeemDeal(voucher) {
            const isOnline = store.state.network?.isOnline ?? true;
            if (!isOnline) {
                redeemStatus.value = {
                    type: 'error',
                    message: 'You are currently offline. Please check your internet connection to redeem vouchers.'
                };
                setTimeout(() => {
                    redeemStatus.value = { type: '', message: '' };
                }, 5000);
                return;
            }

            const voucherId = voucher?.id ?? voucher?.voucher_id ?? voucher?.voucherId;

            if (!voucherId) {
                redeemStatus.value = {
                    type: 'error',
                    message: 'Failed redeeming voucher. Missing voucher ID.'
                };
                return;
            }

            const payload = {
                merchant_id: voucher.merchant_id,
                memberDeal: true,
                quantity: 1,
                voucher_id: voucherId,
                expiration: voucher.expiration,
                cancelable: voucher.cancelable,
            };

            // Tambahkan cancelable_before jika voucher dapat dibatalkan
            if (voucher.cancelable === 1 && voucher.cancellation_cutoff) {
                const expirationDate = new Date(voucher.expiration);
                const cutoffMs = voucher.cancellation_cutoff * 1000;
                const cancelableBeforeDate = new Date(expirationDate.getTime() - cutoffMs);

                const year = cancelableBeforeDate.getFullYear();
                const month = String(cancelableBeforeDate.getMonth() + 1).padStart(2, '0');
                const day = String(cancelableBeforeDate.getDate()).padStart(2, '0');
                const hours = String(cancelableBeforeDate.getHours()).padStart(2, '0');
                const minutes = String(cancelableBeforeDate.getMinutes()).padStart(2, '0');
                const seconds = String(cancelableBeforeDate.getSeconds()).padStart(2, '0');

                payload.cancelable_before = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            }

            redeemStatusModal.value = { visible: true, status: 'loading' };

            store.dispatch('ClientVoucher/redeem', { data: payload })
                .then(async () => {
                    await fetchDashboard();
                    await loadAllData();

                    redeemStatus.value = {
                        type: 'success',
                        message: 'Voucher redeemed successfully.'
                    };
                    setTimeout(() => {
                        redeemStatus.value = { type: '', message: '' };
                    }, 3000);
                    redeemStatusModal.value = { visible: true, status: 'success' };
                })
                .catch((error) => {
                    if (error.response?.data?.message === 'VOUCHER_REDEEM.CREATE_SINGLE_DATA.MODEL.EXCEPTION' &&
                        error.response?.data?.error === 'Member not have enough point to redeem this voucher') {
                        redeemStatus.value = { type: '', message: '' };
                        redeemStatusModal.value = { visible: true, status: 'error' };
                    } else if (!error.response) {
                        store.dispatch('network/probe');
                        redeemStatus.value = {
                            type: 'error',
                            message: 'Failed to redeem voucher due to connection issue. Please check your internet connection.'
                        };
                        redeemStatusModal.value = { visible: false, status: 'loading' };
                        setTimeout(() => {
                            redeemStatus.value = { type: '', message: '' };
                        }, 5000);
                    } else {
                        const errorMessage = getRedeemErrorMessage(error);
                        redeemStatus.value = {
                            type: 'error',
                            message: errorMessage
                        };
                        redeemStatusModal.value = { visible: true, status: 'loading' };
                        setTimeout(() => {
                            redeemStatusModal.value.visible = false;
                        }, 1500);
                    }
                });
        }

        /**
         * Convert technical error messages to user-friendly messages
         * @param {Object} error - Error object from API
         * @returns {string} User-friendly error message
         */
        function getRedeemErrorMessage(error) {
            const apiMessage = error?.response?.data?.message || '';
            const apiError = error?.response?.data?.error || '';
            
            const errorMap = {
                'VOUCHER_POINT_VALIDATE.EXCEPTION': 'You don\'t have enough points to redeem this voucher.',
                'VOUCHER_EXPIRE_VALIDATE.EXCEPTION': 'This voucher has expired.',
                'VOUCHER_VALIDATE.EXCEPTION': 'This voucher cannot be redeemed at this time.'
            };

            if (errorMap[apiMessage]) {
                return errorMap[apiMessage];
            }

            if (apiError.includes('not have enough point')) {
                return 'You don\'t have enough points to redeem this voucher.';
            }

            if (apiError.includes('vouchers left in stock') || apiError.includes('Only')) {
                return apiError;
            }

            if (apiError.includes('expired')) {
                return 'This voucher has expired and can no longer be redeemed.';
            }

            return 'Failed to redeem voucher. Please try again later.';
        }

        function handleViewAllPromotions() {
            allPromotionsModalVisible.value = true;
            pushAllPromotionsModal();
        }

        function closeAllPromotionsModal() {
            allPromotionsModalVisible.value = false;
            popAllPromotionsModal();
        }

        /**
         * Handler untuk menutup modal status redeem
         */
        function handleRedeemStatusClose() {
            redeemStatusModal.value = { visible: false, status: 'loading' };
        }

        /**
         * Mengambil 3 transaksi terbaru berdasarkan tanggal
         * @returns {Array} Array transaksi yang sudah diurutkan
         */
        const getRecentTransactions = () => {
            const currentDashboard = dashboardView.value;
            if (!currentDashboard || !currentDashboard.point_histories) {
                return [];
            }
            
            const limit = isBenefitModuleEnabled.value ? 3 : 5;

            return currentDashboard.point_histories
                .slice()
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, limit);
        };

        const memberName = computed(() => {
             const u = userInfo.value;
             if (u && (u.first_name || u.last_name)) {
                 return `${u.title || ''} ${u.first_name || ''} ${u.last_name || ''}`.trim();
             }
             return null;
        });

        return {
            isBookingModuleEnabled,
            bookingMerchants,
            bookingAuthContext,
            isBookingLoading,
            router,
            dashboardView,
            dashboardData,
            hasDashboardData,
            isModalVisible,
            editedItem,
            isGiftModalOpen,
            giftItem,
            formatGiftDate,
            showModal,
            closeModal,
            getRecentTransactions,
            benefits,
            benefitsToShow,
            remainingBenefitsCount,
            selectedBenefit,
            benefitModalVisible,
            showBenefitDetail,
            closeBenefitModal,
            redirectToBenefits,
            selectedVoucher,
            voucherModalVisible,
            showVoucherDetail,
            closeVoucherModal,
            redeemVoucher,
            redeemStatus,
            redeemStatusModal,
            handleRedeemStatusClose,
            fetchDashboard,
            promotionTitle,
            promotionItems,
            handlePromotionItemClick,
            tierUpgradeModalVisible,
            selectedLockedItem,
            closeTierUpgradeModal,
            handleViewTierBenefits,
            userInfo,
            memberName,
            isPointSystemActive,
            isPointValueEnabled,
            isPointValueVisibleToMember,
            formatCurrency,
            currentTierData,
            allPromotionsModalVisible,
            allPromotionItems,
            handleViewAllPromotions,
            closeAllPromotionsModal,
            newsletters,
            loadingNewsletters,
            isNewsletterModuleEnabled,
            isBenefitModuleEnabled,
            isPromotionsModuleEnabled,
            isPointControlEnabled,
            isMobile
        }
    }
}
</script>